<template>
  <div class="chess-mobile">
    <chess-loader-mobile v-if="props.loading" />

    <template v-else>
      <e-expansion-panels
        v-if="props.accordion"
        :model-value="props.accordionMultiple ? Array.from(Array(props.items.length).keys()) : [0]"
        :multiple="props.accordionMultiple"
        class="chess-mobile__panels"
      >
        <e-expansion-panel v-for="(item, itemIndex) in props.items" :key="itemIndex">
          <template #title> {{ item.title }}</template>
          <div class="chess-mobile__tables">
            <template v-for="(table, tableIndex) in item.tables" :key="tableIndex">
              <button class="chess-mobile-table" @click="handleTableOpen(item, table, table.rows[0])">
                <div>
                  <div class="button-l font-600" v-html="table.fixed?.title" />
                  <div class="paragraph-xs text-secondary" v-html="table.fixed?.label" />
                </div>
                <e-icon name="chevronRight" :size="12" class="text-secondary" />
              </button>
              <divider v-if="tableIndex < item.tables.length - 1" class="my-2" />
            </template>
          </div>
        </e-expansion-panel>
      </e-expansion-panels>

      <div v-else class="chess-mobile__panels">
        <div v-for="(item, itemIndex) in props.items" :key="itemIndex">
          <div v-if="item.title" class="paragraph-l mb-2">
            <b>{{ item.title }}</b>
          </div>
          <div class="chess-mobile__tables">
            <template v-for="(table, tableIndex) in item.tables" :key="tableIndex">
              <template v-for="(row, rowIndex) in table.rows" :key="rowIndex">
                <button class="chess-mobile-table" @click="handleTableOpen(item, table, row)">
                  <div>
                    <div class="button-l font-600" v-html="row.fixed?.title" />
                    <div class="paragraph-xs text-secondary" v-html="row.fixed?.text" />
                  </div>
                  <e-icon name="chevronRight" :size="12" class="text-secondary" />
                </button>
                <divider v-if="rowIndex < table.rows.length - 1" class="my-2" />
              </template>
            </template>
          </div>
        </div>
      </div>
    </template>

    <e-dialog v-model="isDialogOpen">
      <template #title>
        <div v-if="props.dialogTitleType === 'full'" v-html="`${selectedItem?.title}, ${selectedTable?.fixed?.title}`" />
        <div v-else v-html="selectedRow?.fixed?.title" />
      </template>

      <div v-if="props.showPlaceWithTypeSelect" class="chess-mobile__filters">
        <e-select
          :model-value="`${selectedRow?.fixed?.title}, ${selectedRow?.fixed?.text}`"
          :items="placeWithTypeItems"
          label="Место, Тип туриста"
          @update:model-value="onPlaceWithTypeChange"
        />
      </div>

      <chess-calendar
        v-if="selectedTable && selectedRow"
        ref="chessCalendarRef"
        class="mt-6"
        :period="props.period"
        :table="selectedTable"
        :cells="selectedRow.cells"
        :is-edit="props.isEdit"
        :chess-dates-available-from="props.chessDatesAvailableFrom"
        @select="emit('select', $event)"
      />
    </e-dialog>
  </div>
</template>

<script setup lang="ts" generic="T">
import { computed, ref } from 'vue';
import type {
  ChessCalendarComponent,
  ChessDateCellType,
  ChessItemType,
  ChessTableRowType,
  ChessTableType,
} from '@/core/components/interface/Chess/Chess.type';
import type { RangeOfDateOnly } from '@/graphql/generated';
import Divider from '@/core/components/helpers/Divider.vue';
import ChessCalendar from '@/core/components/interface/Chess/ChessCalendar.vue';
import ChessLoaderMobile from '@/core/components/interface/Chess/ChessLoaderMobile.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EExpansionPanel, EExpansionPanels, EIcon, ESelect } from '@/core/components/ui';

import useToggle from '@/core/composables/useToggle';

const props = defineProps<{
  period?: RangeOfDateOnly;
  items: ChessItemType<T>[];
  isEdit?: boolean;
  accordion?: boolean;
  accordionMultiple?: boolean;
  loading?: boolean;
  chessDatesAvailableFrom?: string; // Date in format YYYY-MM-DD
  dialogTitleType?: 'full' | 'row';
  showPlaceWithTypeSelect?: boolean;
}>();

const emit = defineEmits<{
  (event: 'select', value: ChessDateCellType<T>[]): void;
}>();

const [isDialogOpen, setIsDialogOpen] = useToggle();

const selectedItem = ref<ChessItemType<T>>();
const selectedTable = ref<ChessTableType<T>>();
const selectedRow = ref<ChessTableRowType<T>>();

const placeWithTypeItems = computed(() => {
  if (!selectedTable.value) return [];
  const items = selectedTable.value.rows.map(
    (r) => `${r.fixed?.title as string}, ${r.fixed?.text as string}`
  );
  return [...new Set(items)];
});

const onPlaceWithTypeChange = (value: string | null | undefined) => {
  if (!selectedTable.value || !value) return;
  const [place, type] = value.split(', ');
  const row = selectedTable.value.rows.find(
    (r) => r.fixed?.title === place && r.fixed?.text === type
  );
  if (row) {
    selectedRow.value = row;
  }
};

const handleTableOpen = (item: ChessItemType<T>, table: ChessTableType<T>, row: ChessTableRowType<T>) => {
  selectedItem.value = item;
  selectedTable.value = table;
  selectedRow.value = row;
  setIsDialogOpen(true);
};

const chessCalendarRef = ref<ChessCalendarComponent>();

defineExpose({
  scrollTo: () => chessCalendarRef.value?.scrollTo,
});
</script>

<style scoped lang="scss">
.chess-mobile {
  &__panels {
    display: grid;

    grid-template-columns: minmax(0, 1fr);
  }

  &__tables {
    padding: 0 12px;
  }

  &__filters {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

.chess-mobile-table {
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  padding: 12px 0 12px 12px;

  text-align: initial;
}
</style>
